/**
 * Self-Cert Individual XML Generator
 * Updated implementation using user-provided XML template with form field mappings
 */

import { SelfCertIndividualService } from "../../../services/SelfCertIndividualService";
import { SelfCertIndividualFormModel } from "../../../Models/SelfCertIndividualModels";
import { formatDateForXML } from "../../../Utility/utility";
import { getPDFBase64 } from "./generateW8BENXML";

export async function generateSelfCertIndividualXML(data: any): Promise<string> {
  // Check if form data needs mapping
  let formModel: SelfCertIndividualFormModel;
  let isMapDone = data?.isMapDone || false;

  if (!isMapDone) {
    formModel = SelfCertIndividualService.fromApiResponse(data);
  } else {
    formModel = new SelfCertIndividualFormModel(data);
  }

  // Helper function to get field values
  const getValue = (fieldName: string): string => {
    const value = formModel[fieldName] || "";
    return value;
  };

  // Helper function to convert yes/no values to true/false
  const convertYesNoToBoolean = (fieldName: string): string => {
    const value = formModel[fieldName];
    if (value === true || value === "yes" || value === "Yes") {
      return "true";
    }
    if (
      value === false ||
      value === "no" ||
      value === "No" ||
      value === "" ||
      value === null ||
      value === undefined
    ) {
      return "false";
    }
    return "false"; // Default to false for any other value
  };

  // Generate original file XML if viewPdf is provided
  const generateOriginalFileXml = async () => {
    const viewPdfUrl = getValue("viewPdf");
    if (viewPdfUrl) {
      try {
        const originalFileContent = await getPDFBase64(viewPdfUrl, formModel["fileImportId"]);
        const fileName = viewPdfUrl.split("/").pop() || "original_document.pdf";

        return `
        <OriginalFile>
          <Name>${fileName}</Name>
          <Data>${originalFileContent}</Data>
        </OriginalFile>`;
      } catch (error) {
        console.error("Error converting original PDF to base64:", error);
        return "<OriginalFile></OriginalFile>";
      }
    }
    return "<OriginalFile></OriginalFile>";
  };

  const originalFileXml = await generateOriginalFileXml();

  // Get TIN not available checkbox status
  const getTINNotAvailable = (): string => {
    // Check common field names for TIN not available
    const value =
      formModel["sc_cp Unable to procure TIN"] ||
      formModel["SC_Individual_TIN_not_available"] ||
      false;
    return convertYesNoToBoolean("sc_cp Unable to procure TIN");
  };

  // Generate XML using the user-provided template with proper form field mappings
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<Deloitte_US_Individual_2024>
    <BusinessUnit></BusinessUnit>
    <UniqueIdentifier>${getValue("UniqueIdentifier")}</UniqueIdentifier>
    <CommonInfo>
        <Individual>
            <TitleName>${getValue("SC_Individual_Title")}</TitleName>
            <FirstName>${getValue("SC_Individual_First_name")}</FirstName>
            <LastName>${getValue("SC_Individual_Last_name")}</LastName>
            <MiddleName>${getValue("SC_Individual_Middle_name")}</MiddleName>
            <CountryOfBirth>${getValue("SC_Individual_Country_of_birth")}</CountryOfBirth>
            <CountryOfBirthOther>${getValue("SC_Individual_Country_of_birth_other")}</CountryOfBirthOther>
            <DateOfBirth>${getValue("SC_Individual_Date_of_birth")}</DateOfBirth>
            <CityTownOfBirth>${getValue("SC_Individual_City_of_birth")}</CityTownOfBirth>
        </Individual>
        <PermanentAddress>
            <Line1>${getValue("SC_Individual_Current_residence_address_street")}</Line1>
            <Line2>${getValue("SC_Individual_Current_residence_address_line2")}</Line2>
            <City_Town>${getValue("SC_Individual_Current_residence_address_city")}</City_Town>
            <State_Province>${getValue("SC_Individual_Current_residence_address_state")}</State_Province>
            <Country>${getValue("SC_Individual_Current_residence_address_country")}</Country>
            <CountryOther>${getValue("SC_Individual_Current_residence_address_country_other")}</CountryOther>
            <Zip_PostalCode>${getValue("SC_Individual_Current_residence_address_postal")}</Zip_PostalCode>
            <IsCare_of_address>${convertYesNoToBoolean("SC_Individual_Current_residence_care_of")}</IsCare_of_address>
            <IsP_O_Box>${convertYesNoToBoolean("SC_Individual_Current_residence_po_box")}</IsP_O_Box>
            <IsRuralRoute>${convertYesNoToBoolean("SC_Individual_Current_residence_rural_route")}</IsRuralRoute>
        </PermanentAddress>
        <MailingAddress>
            <Line1>${getValue("SC_Individual_Mailing_address_street")}</Line1>
            <Line2>${getValue("SC_Individual_Mailing_address_line2")}</Line2>
            <City_Town>${getValue("SC_Individual_Mailing_address_city")}</City_Town>
            <State_Province>${getValue("SC_Individual_Mailing_address_state")}</State_Province>
            <Country>${getValue("SC_Individual_Mailing_address_country")}</Country>
            <CountryOther>${getValue("SC_Individual_Mailing_address_country_other")}</CountryOther>
            <Zip_PostalCode>${getValue("SC_Individual_Mailing_address_postal")}</Zip_PostalCode>
            <IsCare_of_address>${convertYesNoToBoolean("SC_Individual_Mailing_care_of")}</IsCare_of_address>
            <IsP_O_Box>${convertYesNoToBoolean("SC_Individual_Mailing_po_box")}</IsP_O_Box>
            <IsRuralRoute>${convertYesNoToBoolean("SC_Individual_Mailing_rural_route")}</IsRuralRoute>
        </MailingAddress>
    </CommonInfo>
    <USCitizen>${convertYesNoToBoolean("SC_Individual_US_citizen")}</USCitizen>
    <TaxResidencyCountry>${getValue("sc_cp Jurisdiction 1")}</TaxResidencyCountry>
    <TaxResidencyTINType>${getValue("sc_cp Tax reference number type")}</TaxResidencyTINType>
    <TaxResidencyTIN>${getValue("sc_cp Tax Identification Number")}</TaxResidencyTIN>
    <cbTaxResidencyYes>${convertYesNoToBoolean("sc_cp Sole Residency Yes")}</cbTaxResidencyYes>
    <cbTaxResidencyNo>${convertYesNoToBoolean("sc_cp Sole Residency No")}</cbTaxResidencyNo>
    <cbTaxResidencyTINNotAvailable>${convertYesNoToBoolean("sc_cp Unable to procure TIN")}</cbTaxResidencyTINNotAvailable>
    <TaxResidencyTINNotAvailableReason>${getValue("sc_cp Reason for non-availability of TIN")}</TaxResidencyTINNotAvailableReason>
    <taxCountry1>${getValue("sc_cp Jurisdiction 1")}</taxCountry1>
    <taxCountry1ID>${getValue("sc_cp Tax Identification Number")}</taxCountry1ID>
    <taxCountry1IDType>${getValue("sc_cp Tax reference number type")}</taxCountry1IDType>
    <taxTIN1>${getValue("sc_cp Tax Identification Number")}</taxTIN1>
    <taxCountry2>${getValue("sc_cp Jurisdiction 2")}</taxCountry2>
    <taxCountry2ID>${getValue("sc_cp Tax Identification Number 2")}</taxCountry2ID>
    <taxCountry2IDType>${getValue("sc_cp Tax Reference Number Type 2")}</taxCountry2IDType>
    <taxTIN2>${getValue("sc_cp Tax Identification Number 2")}</taxTIN2>
    <taxCountry3>${getValue("sc_cp Jurisdiction 3")}</taxCountry3>
    <taxCountry3ID>${getValue("sc_cp Tax Identification Number 3")}</taxCountry3ID>
    <taxCountry3IDType>${getValue("sc_cp Tax Reference Number Type 3")}</taxCountry3IDType>
    <taxTIN3>${getValue("sc_cp Tax Identification Number 3")}</taxTIN3>
    <taxCountry4>${getValue("sc_cp Jurisdiction 4")}</taxCountry4>
    <taxCountry4ID>${getValue("sc_cp Tax Identification Number 4")}</taxCountry4ID>
    <taxCountry4IDType>${getValue("sc_cp Tax Reference Number Type 4")}</taxCountry4IDType>
    <AccountID1>${getValue("SC_Individual_Account_ID_1")}</AccountID1>
    <AccountID2>${getValue("SC_Individual_Account_ID_2")}</AccountID2>
    <AccountID3>${getValue("SC_Individual_Account_ID_3")}</AccountID3>
    <Intro>${getValue("Form_name")}</Intro>
    <EntityCertification>${convertYesNoToBoolean("sc_cp Certify Account Holder")}</EntityCertification>
    <NotResidentForTax>${convertYesNoToBoolean("SC_Individual_Not_resident_for_tax")}</NotResidentForTax>
    <USTIN>${getValue("SC_Individual_US_TIN")}</USTIN>
    <TinUnavailableReason>${getValue("sc_cp Reason for unable to procure TIN")}</TinUnavailableReason>
    <SignInfo>
        <IsSigned>${convertYesNoToBoolean("SC_Individual_isSigned")}</IsSigned>
        <SignatureUnreadable>${convertYesNoToBoolean("SC_Individual_Signature_unreadable")}</SignatureUnreadable>
        <By>${getValue("SC_Individual_Signer_printed_name")}</By>
        <Capacity>${getValue("SC_Individual_Capacity")}</Capacity>
        <SignatureUnreadable>${convertYesNoToBoolean("Signature_unreadable")}</SignatureUnreadable>
        <CapacityOther>${getValue("SC_Individual_Capacity_other")}</CapacityOther>
        <Date>${formatDateForXML(getValue("SC_Individual_Date"))}</Date>
        <DateOnForm>${formatDateForXML(getValue("SC_Individual_Date"))}</DateOnForm>
    </SignInfo>
    ${originalFileXml}
    <Form_OCR_Signature>${getValue("SC_Individual_Signature")}</Form_OCR_Signature>
</Deloitte_US_Individual_2024>`;

  console.log("🎯 generateSelfCertIndividualXML final XML:", xml);
  return xml;
}
